/**
 * labelEdit — 문제 편집기 [라벨] 칸의 초안 계산(순수 함수). 화면(ProblemViewTab)은 상태만 쥐고 판단은 여기서 한다.
 *
 * 계약: docs/exam-builder-v2/BE-20260830-reply-ocr-repromote-history-close.md §1 3-1 · 요청서
 *       docs/problem-hub-v2/FE-20260913-concept-difficulty-direct-edit.md §1-6.
 *
 * 모양
 *   server : GET /api/v2/labels/problems/{id} 의 `labels` — [{ label_uuid, label_name, slug, label_type }]
 *   draft  : { added: [{ label_uuid|null, label_name }], removed: [label_uuid] } — 서버에 아직 안 보낸 것
 *   raw    : 상세의 `labels_raw`(콤마 문자열). 사전에 오르지 않는 값(숫자만 등)은 **여기에만** 있다.
 *
 * ⚠ 초안은 늘 **지금의 서버 목록에 대어** 계산한다(labelPlan). 저장이 중간에 멈춰 일부만 반영된 뒤 목록을
 *   다시 읽으면, 이미 된 것은 계획에서 저절로 빠진다 — 초안을 따로 고쳐 쓰지 않아도 된다.
 */
import { splitLabels } from '../ExamPapersV2/labelBadge';

export const EMPTY_LABEL_DRAFT = Object.freeze({ added: [], removed: [] });

/**
 * 서버의 slug 규칙을 옮긴 것 — kyhub `models/models.py:1119` `Tags.slugify` + 호출부의 `[:100]`.
 * ① 글자·숫자·밑줄·공백만 남긴다(하이픈도 지운다) ② 공백 → 하이픈, 겹친 하이픈 하나로, 앞뒤 하이픈 제거
 * ③ 비면 'tag' ④ 소문자.
 * 쓰는 곳은 둘이다: 같은 라벨을 두 번 넣지 않게 가르기, 그리고 사전 생성이 「이미 있다」(409)로 답했을 때
 * 그 라벨을 검색 결과에서 찾아내기(서버가 그 라벨을 돌려주지 않는다 — 요청서 §2-2).
 * **서버 규칙이 바뀌면 조용히 어긋난다** — 그래서 판정의 정본은 늘 서버다. 여기서 틀려도 결과는
 * 「같은 것을 한 번 더 붙이려다 서버가 그대로 둔다」이지 잘못된 라벨이 붙는 것이 아니다.
 */
export function labelSlug(name) {
  let t = String(name ?? '').normalize('NFC').replace(/[^\p{L}\p{N}_\s]/gu, '');
  t = t.trim().replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  if (!t) t = 'tag';
  return t.toLowerCase().slice(0, 100);
}

/**
 * 서버가 라벨로 받지 않는 값 — kyhub `services/label_v2_services.py` EXCLUDE_PATTERNS + 100자 한도.
 * 미리 막는 이유: 사전 생성이 400 으로 거절되면 그 저장은 본문·정답을 쓴 뒤에 멈춘다. 치는 순간 말하는 편이 낫다.
 * 정본은 서버다 — 여기서 통과해도 서버가 거절하면 그 문구를 그대로 낸다(describeLabelError).
 */
const LABEL_EXCLUDE = [
  [/^\d+$/, '숫자만으로 된 값은 라벨이 되지 않습니다'],
  [/^\d+(_\d+)+$/, '「12_3」 꼴 코드는 라벨이 되지 않습니다'],
  [/^task\d+$/i, 'AI 작업 기록 꼬리표는 라벨이 되지 않습니다'],
  [/^(aiagent|prob2prob|concept2prob|unknown)$/i, 'AI 작업 기록 꼬리표는 라벨이 되지 않습니다'],
  [/^completed$/i, '상태값은 라벨이 되지 않습니다'],
];

export function labelRejectReason(name) {
  const t = String(name ?? '').normalize('NFC').trim();
  if (!t) return '빈 값은 넣을 수 없습니다';
  const hit = LABEL_EXCLUDE.find(([re]) => re.test(t));
  if (hit) return hit[1];
  if ([...t].length > 100) return '100자를 넘는 라벨은 만들 수 없습니다';
  return null;
}

const slugOf = (l) => l?.slug || labelSlug(l?.label_name);

/**
 * 지금 보낼 것 — 초안을 서버 목록에 대어 걸러 낸다.
 *  · removed: 서버에 아직 붙어 있는 것만
 *  · added  : 이미 붙어 있는 것과 같은 것(slug)은 뺀다. 뗀다고 표시한 것과 같은 것을 다시 넣었으면
 *             **둘 다** 뺀다 — 떼었다 붙이는 왕복을 서버에 보내지 않는다.
 */
export function labelPlan(server, draft) {
  const list = Array.isArray(server) ? server : [];
  const d = draft || EMPTY_LABEL_DRAFT;
  const removedSet = new Set(d.removed);
  const addedSlugs = new Set(d.added.map((a) => labelSlug(a.label_name)));
  // 뗀 것과 같은 것을 다시 넣었다 — 그 라벨은 그대로 둔다
  const cancelled = new Set(list.filter((l) => removedSet.has(l.label_uuid) && addedSlugs.has(slugOf(l))).map((l) => l.label_uuid));
  const removed = list.filter((l) => removedSet.has(l.label_uuid) && !cancelled.has(l.label_uuid))
    .map((l) => ({ label_uuid: l.label_uuid, label_name: l.label_name }));
  const present = new Set(list.map(slugOf));
  const seen = new Set();
  const added = [];
  d.added.forEach((a) => {
    const s = labelSlug(a.label_name);
    if (present.has(s) || seen.has(s)) return;
    seen.add(s);
    added.push({ label_uuid: a.label_uuid || null, label_name: a.label_name });
  });
  return { removed, added };
}

export const isLabelPlanEmpty = (plan) => !plan || (plan.removed.length === 0 && plan.added.length === 0);

/**
 * 칩으로 세울 것 — 잠긴 값(사전 밖) · 붙어 있는 것(뗀 것 빼고) · 새로 넣은 것.
 * 잠긴 값: `labels_raw` 에는 있는데 사전 목록에 없는 것. 부착·해제 API 가 문자열의 그 값은 건드리지 않으므로
 * (kyhub `api/v2/labels.py:139-146` — 문자열 토큰을 그대로 두고 slug 가 같은 것만 뗀다) 여기서도 뗄 수 없다.
 */
export function labelChips(server, draft, raw) {
  const list = Array.isArray(server) ? server : [];
  const plan = labelPlan(list, draft);
  const removedSet = new Set(plan.removed.map((l) => l.label_uuid));
  const names = new Set(list.map((l) => l.label_name));
  const slugs = new Set(list.map(slugOf));
  const locked = splitLabels(raw).filter((t) => !names.has(t) && !slugs.has(labelSlug(t)));
  return [
    ...locked.map((t) => ({ key: `raw:${t}`, text: t, locked: true })),
    ...list.filter((l) => !removedSet.has(l.label_uuid)).map((l) => ({ key: l.label_uuid, text: l.label_name })),
    ...plan.added.map((a) => ({ key: `new:${labelSlug(a.label_name)}`, text: a.label_name, isNew: !a.label_uuid })),
  ];
}

/**
 * 넣기 — 문자열(친 것)이나 { label_uuid, label_name }(사전에서 고른 것)을 받는다.
 * 반환 { draft, rejected: [{ text, reason }] }. 이미 있는 것은 조용히 건너뛴다(칩이 이미 보인다).
 */
export function addLabelTokens(server, draft, tokens) {
  const list = Array.isArray(server) ? server : [];
  const d = draft || EMPTY_LABEL_DRAFT;
  let removed = [...d.removed];
  const added = [...d.added];
  const rejected = [];
  (tokens || []).forEach((tok) => {
    const item = typeof tok === 'string'
      ? { label_uuid: null, label_name: tok.normalize('NFC').trim() }
      : { label_uuid: tok?.label_uuid || null, label_name: String(tok?.label_name ?? '').normalize('NFC').trim() };
    const reason = labelRejectReason(item.label_name);
    if (reason) { rejected.push({ text: item.label_name, reason }); return; }
    const s = labelSlug(item.label_name);
    const onServer = list.find((l) => slugOf(l) === s);
    if (onServer) {
      // 떼기로 표시해 둔 것을 다시 넣었다 — 표시만 거둔다
      if (removed.includes(onServer.label_uuid)) removed = removed.filter((u) => u !== onServer.label_uuid);
      return;
    }
    if (added.some((a) => labelSlug(a.label_name) === s)) return;
    added.push(item);
  });
  return { draft: { added, removed }, rejected };
}

/** 칩 떼기 — 새로 넣은 것은 초안에서 지우고, 붙어 있던 것은 떼기로 표시한다. 잠긴 값은 그대로. */
export function removeLabelChip(server, draft, key) {
  const d = draft || EMPTY_LABEL_DRAFT;
  const k = String(key ?? '');
  if (k.startsWith('raw:')) return d;
  if (k.startsWith('new:')) {
    const s = k.slice(4);
    return { ...d, added: d.added.filter((a) => labelSlug(a.label_name) !== s) };
  }
  const list = Array.isArray(server) ? server : [];
  if (!list.some((l) => l.label_uuid === k) || d.removed.includes(k)) return d;
  return { ...d, removed: [...d.removed, k] };
}

/** 넣지 못한 값들을 한 줄로 — 「3」 — 숫자만으로 된 값은 … · 「…」 — … */
export function describeRejectedLabels(rejected) {
  return (rejected || []).map((x) => `「${x.text}」 — ${x.reason}`).join(' · ');
}

/**
 * 라벨 저장 실패를 사람 말로 — applyLabelPlan 이 실은 step·label·httpStatus 로 가른다.
 * 서버 문구는 가능한 한 그대로 둔다: 무엇이 왜 걸렸는지는 서버가 가장 정확하다.
 */
export function describeLabelError(e) {
  const name = e?.label ? `「${e.label}」` : '';
  if (e?.step === 'attach') {
    if (e.httpStatus === 409) return '라벨을 합친 길이가 255자를 넘어 붙이지 못했습니다 — 긴 라벨을 먼저 떼고 다시 저장하세요.';
    if (e.httpStatus === 404) return '사전에서 사라진 라벨이 섞여 붙이지 못했습니다 — 편집을 취소하고 다시 시도하세요.';
    return `라벨을 붙이지 못했습니다 — ${e.message || '다시 시도하세요.'}`;
  }
  if (e?.step === 'create') return `${name} 라벨을 만들지 못했습니다 — ${e.message || '다시 시도하세요.'}`;
  if (e?.step === 'detach') return `${name} 라벨을 떼지 못했습니다 — ${e.message || '다시 시도하세요.'}`;
  return e?.message || '라벨 저장에 실패했습니다.';
}
